import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bot, X, Send, Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/i18n/LanguageContext";
import { toast } from "@/hooks/use-toast";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
}

export function VoiceChatbot() {
  const { t, language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [showHandoff, setShowHandoff] = useState(false);
  const [handoffName, setHandoffName] = useState("");
  const [handoffEmail, setHandoffEmail] = useState("");
  const [handoffPhone, setHandoffPhone] = useState("");
  const [handoffMessage, setHandoffMessage] = useState("");
  const [handoffSubmitting, setHandoffSubmitting] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "welcome",
      role: "assistant",
      content: t.chatbot.welcome,
    },
  ]);

  // Update welcome message when language changes
  useEffect(() => {
    setMessages(prev =>
      prev.map(m =>
        m.id === "welcome" ? { ...m, content: t.chatbot.welcome } :
        m.id === "auto-greeting" ? { ...m, content: t.chatbot.autoOpen } :
        m
      )
    );
  }, [language, t.chatbot.welcome, t.chatbot.autoOpen]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Auto-open after 30 seconds (once per session)
  useEffect(() => {
    const hasAutoOpened = localStorage.getItem("chatbot-auto-opened");
    if (hasAutoOpened) return;

    const autoOpenTimer = setTimeout(() => {
      if (!isOpen) {
        setIsOpen(true);
        localStorage.setItem("chatbot-auto-opened", "true");
        setMessages([
          { id: "welcome", role: "assistant", content: t.chatbot.welcome },
          { id: "auto-greeting", role: "assistant", content: t.chatbot.autoOpen },
        ]);
      }
    }, 30000);

    return () => clearTimeout(autoOpenTimer);
  }, [isOpen, t.chatbot.welcome, t.chatbot.autoOpen]);

  const handleSendMessage = async (text: string) => {
    if (!text.trim() || isTyping) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: text.trim(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue("");
    setIsTyping(true);

    try {
      const history = messages
        .filter(m => m.id !== "welcome" && m.id !== "auto-greeting")
        .map(m => ({ role: m.role, content: m.content }));

      const { data, error } = await supabase.functions.invoke('ai-chat', {
        body: { message: text.trim(), history, language },
      });

      if (error) throw error;

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: data.response || t.chatbot.noResponse,
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('AI chat error:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: t.chatbot.error,
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(inputValue);
    }
  };

  const handleHandoffSubmit = async () => {
    if (!handoffName.trim() || !handoffEmail.trim() || handoffSubmitting) return;
    setHandoffSubmitting(true);
    try {
      const transcript = messages
        .filter(m => m.id !== "welcome" && m.id !== "auto-greeting")
        .map(m => `${m.role === "user" ? "Host" : "Mlynář (AI)"}: ${m.content}`)
        .join("\n\n");

      const composedMessage =
        (handoffMessage.trim() ? `${handoffMessage.trim()}\n\n` : "") +
        `--- Přepis konverzace s AI asistentem ---\n${transcript || "(žádné zprávy)"}`;

      const { error } = await supabase.functions.invoke("send-contact", {
        body: {
          name: handoffName.trim(),
          email: handoffEmail.trim(),
          phone: handoffPhone.trim() || undefined,
          eventType: "Dotaz z chatbota",
          message: composedMessage,
        },
      });

      if (error) throw error;

      toast({ title: t.chatbot.handoffSuccess });
      setHandoffName("");
      setHandoffEmail("");
      setHandoffPhone("");
      setHandoffMessage("");
      setShowHandoff(false);
      setMessages(prev => [
        ...prev,
        {
          id: `handoff-${Date.now()}`,
          role: "assistant",
          content: t.chatbot.handoffSuccess,
        },
      ]);
    } catch (err) {
      console.error("Chatbot handoff error:", err);
      toast({ title: t.chatbot.handoffError, variant: "destructive" });
    } finally {
      setHandoffSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating Button */}
      <motion.button
        onClick={() => setIsOpen(true)}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        aria-label="Open chatbot"
        className={`fixed bottom-6 right-6 z-40 w-16 h-16 rounded-full bg-gradient-gold text-primary-foreground shadow-gold-lg flex items-center justify-center ${
          isOpen ? "hidden" : ""
        }`}
      >
        <span className="absolute inset-0 rounded-full bg-primary/50 animate-ping" />
        <span className="relative">
          <Bot className="w-7 h-7" />
        </span>
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-6 right-6 z-50 w-[360px] max-w-[calc(100vw-48px)] h-[540px] max-h-[calc(100vh-100px)] bg-card border border-border rounded-2xl shadow-gold-lg flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border bg-muted/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-gold flex items-center justify-center">
                  <Bot className="w-5 h-5 text-primary-foreground" />
                </div>
                <div>
                  <h3 className="font-display text-lg">{t.chatbot.name}</h3>
                  <p className="text-xs text-muted-foreground">{t.chatbot.role}</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {showHandoff ? (
              /* Handoff form */
              <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3">
                <div>
                  <h4 className="font-display text-base mb-1">{t.chatbot.handoffTitle}</h4>
                  <p className="text-xs text-muted-foreground">{t.chatbot.handoffSubtitle}</p>
                </div>
                <Input
                  placeholder={t.chatbot.handoffName}
                  value={handoffName}
                  onChange={e => setHandoffName(e.target.value)}
                  maxLength={100}
                  disabled={handoffSubmitting}
                />
                <Input
                  type="email"
                  placeholder={t.chatbot.handoffEmail}
                  value={handoffEmail}
                  onChange={e => setHandoffEmail(e.target.value)}
                  maxLength={255}
                  disabled={handoffSubmitting}
                />
                <Input
                  type="tel"
                  placeholder={t.chatbot.handoffPhone}
                  value={handoffPhone}
                  onChange={e => setHandoffPhone(e.target.value)}
                  maxLength={50}
                  disabled={handoffSubmitting}
                />
                <Textarea
                  placeholder={t.chatbot.handoffMessage}
                  value={handoffMessage}
                  onChange={e => setHandoffMessage(e.target.value)}
                  maxLength={1000}
                  rows={3}
                  disabled={handoffSubmitting}
                />
                <div className="flex gap-2 pt-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => setShowHandoff(false)}
                    disabled={handoffSubmitting}
                  >
                    {t.chatbot.handoffCancel}
                  </Button>
                  <Button
                    size="sm"
                    className="flex-1 bg-gradient-gold text-primary-foreground"
                    onClick={handleHandoffSubmit}
                    disabled={!handoffName.trim() || !handoffEmail.trim() || handoffSubmitting}
                  >
                    {handoffSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : t.chatbot.handoffSubmit}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {/* Messages */}
                <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
                  {messages.map((message) => (
                    <motion.div
                      key={message.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex ${
                        message.role === "user" ? "justify-end" : "justify-start"
                      }`}
                    >
                      <div
                        className={`max-w-[85%] p-3 rounded-2xl text-sm ${
                          message.role === "user"
                            ? "bg-primary text-primary-foreground rounded-br-sm"
                            : "bg-muted rounded-bl-sm"
                        }`}
                      >
                        {message.content}
                      </div>
                    </motion.div>
                  ))}
                  {isTyping && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex justify-start"
                    >
                      <div className="bg-muted rounded-2xl rounded-bl-sm p-3 flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm text-muted-foreground">{t.chatbot.thinking}</span>
                      </div>
                    </motion.div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Handoff CTA */}
                <div className="shrink-0 px-3 pt-2 border-t border-border">
                  <button
                    onClick={() => setShowHandoff(true)}
                    className="w-full flex items-center justify-center gap-2 text-xs px-3 py-2 bg-primary/10 text-primary rounded-md hover:bg-primary/20 transition-colors font-medium"
                  >
                    <Mail className="w-3.5 h-3.5" />
                    {t.chatbot.handoffBtn}
                  </button>
                </div>

                {/* Quick Questions */}
                <div className="shrink-0 px-3 py-2">
                  <div className="flex flex-wrap gap-1.5">
                    {(t.chatbot.quickQuestions as string[]).map((question: string) => (
                      <button
                        key={question}
                        onClick={() => handleSendMessage(question)}
                        disabled={isTyping}
                        className="text-xs px-2.5 py-1 bg-muted rounded-full hover:bg-primary/20 hover:text-primary transition-colors disabled:opacity-50"
                      >
                        {question}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Text Input */}
                <div className="shrink-0 p-3 border-t border-border">
                  <div className="flex items-center gap-2">
                    <Input
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder={t.chatbot.placeholder}
                      className="flex-1"
                      disabled={isTyping}
                    />
                    <Button
                      onClick={() => handleSendMessage(inputValue)}
                      size="icon"
                      disabled={!inputValue.trim() || isTyping}
                      className="shrink-0"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
